import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import {
  IChatConversation,
  IChatConversationScope,
  IConversationReferenceAnnotation
} from '../../../model/chat-conversation';
import { IOpenai } from '../../../model/configuration/openai';
import { serializeExperimentAnnotationsContext } from '../../agent/experiment-annotations-context.service';
import { AuthService } from '../../auth/auth.service';
import { ConfigurationService } from '../../configuration/configuration.service';
import { LogService } from '../../loggers/logger.service';
import {
  ICopilotConversationReferenceSyncRequest,
  ThingieService
} from '../../thingie/thingie-service.service';
import { UserDataService } from '../../user-data/user-data.service';
import {
  ActionProposal,
  AIProvider,
  ChatHiddenContext,
  ChatMessage,
  ChatMessageContentItem,
  ChatService,
  ChatSettings,
  ReasoningEffort,
  SetupMeasurementGoal,
  SetupProjectOption,
  SetupPendingQuestion,
  StreamingEvent
} from '../chat.service';
import { DefaultConfig } from 'src/assets/default.config';

@Injectable()
export class ChatServiceImpl implements ChatService {
  private cachedSettings: IOpenai | null = null;
  private readonly scopedConversationUrls = DefaultConfig.uris.copilot;

  constructor(
    private auth: AuthService,
    private configurationService: ConfigurationService,
    private httpClient: HttpClient,
    private keycloak: KeycloakService,
    private log: LogService,
    private thingieService: ThingieService,
    private userDataService: UserDataService
  ) {}

  private async fetchOpenaiSettings(): Promise<IOpenai> {
    if (this.cachedSettings) {
      return this.cachedSettings;
    }
    try {
      this.cachedSettings = await this.configurationService.getOpenaiSettings();
      return this.cachedSettings;
    } catch (error) {
      this.log.error('Failed to fetch OpenAI settings from backend', error);
      throw error;
    }
  }

  private invalidateCache(): void {
    this.cachedSettings = null;
  }

  public async hasApiKey(provider: AIProvider = 'openai'): Promise<boolean> {
    try {
      switch (provider) {
        case 'openai': {
          const settings = await this.fetchOpenaiSettings();
          return !!settings.hasApiKey;
        }
        case 'anthropic': {
          const settings = await this.configurationService.getAnthropicSettings();
          return !!settings.hasApiKey;
        }
        case 'gemini': {
          const settings = await this.configurationService.getGeminiSettings();
          return !!settings.hasApiKey;
        }
        case 'ollama':
          return true;
        default:
          return false;
      }
    } catch {
      return false;
    }
  }

  public async getSettings(): Promise<ChatSettings> {
    const settings = await this.fetchOpenaiSettings();
    const effort = settings.reasoningEffort as ReasoningEffort;
    const validEfforts: ReasoningEffort[] = ['none', 'low', 'medium', 'high', 'xhigh'];
    return {
      model: settings.model || 'gpt-5.4',
      reasoningEffort: validEfforts.includes(effort) ? effort : 'medium',
      promptId: settings.promptId || null,
      workflowId: settings.workflowId || null
    };
  }

  private getStreamUrl(provider: AIProvider): string {
    const urls = DefaultConfig.streamEndpoints;
    switch (provider) {
      case 'openai': return urls.openai;
      case 'anthropic': return urls.anthropic;
      case 'gemini': return urls.gemini;
      case 'ollama': return urls.ollama;
    }
  }

  public sendMessage(
    message: string,
    images: string[],
    conversationHistory: ChatMessage[],
    provider: AIProvider,
    model?: string,
    hiddenContext?: ChatHiddenContext
  ): Observable<StreamingEvent> {
    return new Observable<StreamingEvent>((subscriber) => {
      let abortController: AbortController | null = new AbortController();

      const runStream = async () => {
        try {
          const streamUrl = this.getStreamUrl(provider);

          const historyForBackend = conversationHistory.map(msg => ({
            role: msg.role,
            content: this.serializeMessageContent(msg.content),
            hiddenContext: this.serializeHiddenContext(msg.hiddenContext, { stripGraphAgentContext: true })
          }));

          const requestBody: Record<string, unknown> = {
            message,
            images,
            conversationHistory: historyForBackend,
          };
          const serializedHiddenContext = this.serializeHiddenContext(hiddenContext);
          const serializedExperimentSampleToolContext = this.serializeExperimentSampleToolContext(hiddenContext);
          if (serializedHiddenContext) {
            requestBody.hiddenContext = serializedHiddenContext;
          }
          if (serializedExperimentSampleToolContext) {
            requestBody.experimentSampleToolContext = serializedExperimentSampleToolContext;
          }
          if (model) {
            requestBody.model = model;
          }

          this.log.debug('Sending streaming message via backend proxy', { provider, historyLength: conversationHistory.length });

          await this.keycloak.updateToken(5);
          const token = await this.keycloak.getToken();

          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
            signal: abortController?.signal,
          });

          if (!response.ok) {
            const errorBody = await response.text().catch(() => '');
            if (response.status === 401) {
              this.invalidateCache();
              subscriber.error(new Error('Authentication failed. Please log in again.'));
            } else if (response.status === 504) {
              if (provider === 'ollama') {
                subscriber.error(new Error(
                  'The Ollama request timed out at the proxy while the local model was still working. '
                  + 'Try again, or increase the proxy timeout if local generations are expected to take longer.'
                ));
              } else if (provider === 'openai' || provider === 'anthropic') {
                subscriber.error(new Error(
                  `The ${provider === 'openai' ? 'OpenAI' : 'Anthropic'} request timed out at the proxy while provider-side analysis may still have been running. `
                  + 'Try again. If this keeps happening, the backend proxy timeout likely needs to be increased.'
                ));
              } else {
                subscriber.error(new Error(`API error: HTTP ${response.status}`));
              }
            } else {
              subscriber.error(new Error(errorBody || `API error: HTTP ${response.status}`));
            }
            return;
          }

          if (!response.body) {
            subscriber.error(new Error('No response body from streaming proxy'));
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let accumulatedReasoning = '';
          let accumulatedText = '';
          let hasEmittedReasoningStart = false;
          let hasEmittedResponseStart = false;

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmedLine = line.trim();

              if (!trimmedLine || trimmedLine.startsWith(':')) {
                continue;
              }

              if (trimmedLine.startsWith('data: ')) {
                const jsonStr = trimmedLine.slice(6);

                if (jsonStr === '[DONE]') {
                  const finalMessage: ChatMessage = {
                    role: 'assistant',
                    content: accumulatedText,
                    reasoning: accumulatedReasoning || undefined
                  };
                  subscriber.next({ type: 'completed', message: finalMessage });
                  subscriber.complete();
                  return;
                }

                try {
                  const event = JSON.parse(jsonStr);
                  const eventType = event.type;

                  switch (eventType) {
                    case 'reasoning_delta':
                      if (!hasEmittedReasoningStart) {
                        subscriber.next({ type: 'status', status: 'reasoning_started' });
                        hasEmittedReasoningStart = true;
                      }
                      if (event.delta) {
                        accumulatedReasoning += event.delta;
                        subscriber.next({ type: 'reasoning_delta', delta: event.delta });
                      }
                      break;

                    case 'text_delta':
                      if (!hasEmittedResponseStart) {
                        subscriber.next({ type: 'status', status: 'response_started' });
                        hasEmittedResponseStart = true;
                      }
                      if (event.delta) {
                        accumulatedText += event.delta;
                        subscriber.next({ type: 'text_delta', delta: event.delta });
                      }
                      break;

                    case 'status':
                      subscriber.next({ type: 'status', status: event.status });
                      break;

                    case 'action_proposal':
                      if (Array.isArray(event.actions)) {
                        subscriber.next({ type: 'action_proposal', actions: event.actions as ActionProposal[] });
                      }
                      break;

                    case 'completed':
                      if (event.message) {
                        subscriber.next({ type: 'completed', message: event.message, promptAudit: event.promptAudit });
                        subscriber.complete();
                        return;
                      }
                      break;

                    case 'error':
                      subscriber.next({ type: 'error', error: event.error || 'Unknown error' });
                      break;

                    default:
                      break;
                  }
                } catch (parseError) {
                  this.log.warn('Failed to parse SSE event', { jsonStr, parseError });
                }
              }
            }
          }

          const finalMessage: ChatMessage = {
            role: 'assistant',
            content: accumulatedText,
            reasoning: accumulatedReasoning || undefined
          };
          subscriber.next({ type: 'completed', message: finalMessage });
          subscriber.complete();

        } catch (error) {
          if ((error as Error).name === 'AbortError') {
            subscriber.complete();
            return;
          }
          this.log.error('Streaming error', error);
          subscriber.error(error instanceof Error ? error : new Error('An unexpected error occurred'));
        }
      };

      void runStream();

      return () => {
        if (abortController) {
          abortController.abort();
          abortController = null;
        }
      };
    });
  }

  private serializeMessageContent(content: ChatMessage['content']): ChatMessageContentItem[] {
    if (typeof content === 'string') {
      return content
        ? [{ type: 'text', text: content }]
        : [];
    }

    return content
      .map((item): ChatMessageContentItem | null => {
        if (item.type === 'text' && item.text) {
          return { type: 'text', text: item.text };
        }
        if (item.type === 'image_url' && item.image_url?.url) {
          return {
            type: 'image_url',
            image_url: { url: item.image_url.url }
          };
        }
        return null;
      })
      .filter((item): item is ChatMessageContentItem => item !== null);
  }

  private serializeHiddenContext(
    hiddenContext?: ChatHiddenContext,
    options?: { stripGraphAgentContext?: boolean }
  ): ChatHiddenContext | undefined {
    const trimmedChartDataCsv = hiddenContext?.chartDataCsv?.trim();
    const trimmedChartAnalysisCsv = hiddenContext?.chartAnalysis?.csvContent?.trim();
    const trimmedChartAnalysisFilename = hiddenContext?.chartAnalysis?.csvFilename?.trim();
    const serializedGraphAgentContext = options?.stripGraphAgentContext
      ? undefined
      : this.serializeGraphAgentContext(hiddenContext?.graphAgentContext);
    const serializedSetupCopilotContext = this.serializeSetupCopilotContext(hiddenContext?.setupCopilotContext);
    const serializedSelectedTool = hiddenContext?.selectedTool
      ? {
        groupId: hiddenContext.selectedTool.groupId,
        toolId: hiddenContext.selectedTool.toolId
      }
      : undefined;

    if (!trimmedChartDataCsv
      && !(trimmedChartAnalysisCsv && trimmedChartAnalysisFilename)
      && !serializedGraphAgentContext
      && !serializedSetupCopilotContext
      && !serializedSelectedTool) {
      return undefined;
    }

    const serializedHiddenContext: ChatHiddenContext = {};
    if (trimmedChartDataCsv) {
      serializedHiddenContext.chartDataCsv = trimmedChartDataCsv;
    }
    if (trimmedChartAnalysisCsv && trimmedChartAnalysisFilename && hiddenContext?.chartAnalysis) {
      serializedHiddenContext.chartAnalysis = {
        csvFilename: trimmedChartAnalysisFilename,
        csvContent: trimmedChartAnalysisCsv,
        requestedToolMode: hiddenContext.chartAnalysis.requestedToolMode
      };
    }
    if (serializedGraphAgentContext) {
      serializedHiddenContext.graphAgentContext = serializedGraphAgentContext;
    }
    if (serializedSetupCopilotContext) {
      serializedHiddenContext.setupCopilotContext = serializedSetupCopilotContext;
    }
    if (serializedSelectedTool) {
      serializedHiddenContext.selectedTool = serializedSelectedTool;
    }

    return serializedHiddenContext;
  }

  private serializeSetupCopilotContext(
    setupCopilotContext?: ChatHiddenContext['setupCopilotContext']
  ): ChatHiddenContext['setupCopilotContext'] | undefined {
    if (!setupCopilotContext) {
      return undefined;
    }

    const measurementGoals = (setupCopilotContext.measurementGoals ?? [])
      .map(goal => goal.trim())
      .filter((goal): goal is SetupMeasurementGoal => Boolean(goal));
    const requestedTemplateHints = (setupCopilotContext.requestedTemplateHints ?? [])
      .map(hint => hint.trim())
      .filter(Boolean);
    const availableProjects = (setupCopilotContext.availableProjects ?? [])
      .slice(0, 10)
      .map(project => ({
        projectId: project.projectId.trim(),
        projectTitle: project.projectTitle.trim()
      }))
      .filter((project): project is SetupProjectOption => Boolean(project.projectId && project.projectTitle));
    const recommendations = (setupCopilotContext.recommendations ?? [])
      .slice(0, 3)
      .map(recommendation => ({
        templateId: recommendation.templateId.trim(),
        templateName: recommendation.templateName.trim(),
        organismClass: recommendation.organismClass,
        matchedGoals: (recommendation.matchedGoals ?? [])
          .map(goal => goal.trim())
          .filter((goal): goal is SetupMeasurementGoal => Boolean(goal)),
        requiredDeviceTypes: (recommendation.requiredDeviceTypes ?? []).map(deviceType => deviceType.trim()).filter(Boolean),
        reasons: (recommendation.reasons ?? [])
          .map(reason => reason.trim())
          .filter(Boolean)
          .slice(0, 3)
      }))
      .filter(recommendation => recommendation.templateId && recommendation.templateName);
    const draft = setupCopilotContext.draft
      ? {
        templateId: setupCopilotContext.draft.templateId.trim(),
        templateName: setupCopilotContext.draft.templateName.trim(),
        experimentName: setupCopilotContext.draft.experimentName.trim(),
        projectId: setupCopilotContext.draft.projectId.trim(),
        projectTitle: setupCopilotContext.draft.projectTitle.trim(),
        replicateCount: setupCopilotContext.draft.replicateCount,
        organismName: setupCopilotContext.draft.organismName?.trim() || undefined,
        organismClass: setupCopilotContext.draft.organismClass,
        measurementGoals: (setupCopilotContext.draft.measurementGoals ?? [])
          .map(goal => goal.trim())
          .filter((goal): goal is SetupMeasurementGoal => Boolean(goal)),
        requiredDeviceTypes: (setupCopilotContext.draft.requiredDeviceTypes ?? []).map(deviceType => deviceType.trim()).filter(Boolean)
      }
      : undefined;

    const serializedSetupCopilotContext = {
      organismName: setupCopilotContext.organismName?.trim() || undefined,
      organismClass: setupCopilotContext.organismClass,
      measurementGoals: measurementGoals.length > 0 ? measurementGoals : undefined,
      requestedTemplateHints: requestedTemplateHints.length > 0 ? requestedTemplateHints : undefined,
      availableProjects: availableProjects.length > 0 ? availableProjects : undefined,
      experimentName: setupCopilotContext.experimentName?.trim() || undefined,
      replicateCount: setupCopilotContext.replicateCount,
      projectId: setupCopilotContext.projectId?.trim() || undefined,
      projectTitle: setupCopilotContext.projectTitle?.trim() || undefined,
      immediateCreateRequested: setupCopilotContext.immediateCreateRequested === true ? true : undefined,
      recommendationOnly: setupCopilotContext.recommendationOnly === true ? true : undefined,
      perReplicateOverridesRequested: setupCopilotContext.perReplicateOverridesRequested === true ? true : undefined,
      pendingQuestion: (setupCopilotContext.pendingQuestion?.trim() || undefined) as SetupPendingQuestion | undefined,
      recommendations: recommendations.length > 0 ? recommendations : undefined,
      draft
    };

    if (!serializedSetupCopilotContext.organismName
      && !serializedSetupCopilotContext.organismClass
      && !serializedSetupCopilotContext.measurementGoals?.length
      && !serializedSetupCopilotContext.requestedTemplateHints?.length
      && !serializedSetupCopilotContext.availableProjects?.length
      && !serializedSetupCopilotContext.experimentName
      && serializedSetupCopilotContext.replicateCount === undefined
      && !serializedSetupCopilotContext.projectId
      && !serializedSetupCopilotContext.projectTitle
      && !serializedSetupCopilotContext.immediateCreateRequested
      && !serializedSetupCopilotContext.recommendationOnly
      && !serializedSetupCopilotContext.perReplicateOverridesRequested
      && !serializedSetupCopilotContext.pendingQuestion
      && !serializedSetupCopilotContext.recommendations
      && !serializedSetupCopilotContext.draft) {
      return undefined;
    }

    return serializedSetupCopilotContext;
  }

  private serializeGraphAgentContext(hiddenContext?: ChatHiddenContext['graphAgentContext']): ChatHiddenContext['graphAgentContext'] | undefined {
    if (!hiddenContext?.thingieId || !hiddenContext.thingieName.trim() || !hiddenContext.experimentName.trim()) {
      return undefined;
    }

    const availableSeries = (hiddenContext.availableSeries ?? [])
      .map(series => ({
        seriesKey: series.seriesKey.trim(),
        thingieId: series.thingieId?.trim() || undefined,
        name: series.name.trim(),
        replicateName: series.replicateName?.trim() || undefined,
        path: series.path.trim(),
        taskLabel: series.taskLabel?.trim() || undefined,
        valueType: series.valueType.trim(),
        processIndex: series.processIndex,
        taskIndex: series.taskIndex,
        output: series.output.trim(),
        sampleBundleKey: series.sampleBundleKey?.trim() || undefined
      }))
      .filter(series => series.seriesKey && series.name && series.output);

    const currentTabs = (hiddenContext.currentTabs ?? [])
      .map(tab => ({
        uuid: tab.uuid.trim(),
        label: tab.label.trim(),
        seriesCount: tab.seriesCount,
        timeReference: tab.timeReference.trim()
      }))
      .filter(tab => tab.uuid);

    const taskSummaries = (hiddenContext.experimentSummary?.taskSummaries ?? [])
      .map(task => ({
        processIndex: task.processIndex,
        taskIndex: task.taskIndex,
        taskLabel: task.taskLabel.trim(),
        deviceType: task.deviceType?.trim() || undefined
      }))
      .filter(task => task.taskLabel);

    const experimentSummary = hiddenContext.experimentSummary
      ? {
        replicateCount: hiddenContext.experimentSummary.replicateCount,
        replicateNames: (hiddenContext.experimentSummary.replicateNames ?? []).map(name => name.trim()).filter(Boolean),
        templateName: hiddenContext.experimentSummary.templateName?.trim() || undefined,
        processCount: hiddenContext.experimentSummary.processCount,
        taskSummaries,
        state: hiddenContext.experimentSummary.state?.trim() || undefined,
        created: hiddenContext.experimentSummary.created?.trim() || undefined
      }
      : undefined;

    const experimentComparison = hiddenContext.experimentComparison
      ? {
        targetExperimentName: hiddenContext.experimentComparison.targetExperimentName.trim(),
        current: hiddenContext.experimentComparison.current,
        target: hiddenContext.experimentComparison.target,
        differences: (hiddenContext.experimentComparison.differences ?? []).map(diff => diff.trim()).filter(Boolean)
      }
      : undefined;
    const experimentSampleManifest = hiddenContext.experimentSampleData?.manifest
      ? {
        experimentGroupId: hiddenContext.experimentSampleData.manifest.experimentGroupId.trim(),
        experimentName: hiddenContext.experimentSampleData.manifest.experimentName.trim(),
        samplePointsPerSeries: hiddenContext.experimentSampleData.manifest.samplePointsPerSeries,
        bundleCount: hiddenContext.experimentSampleData.manifest.bundleCount,
        totalSeriesCount: hiddenContext.experimentSampleData.manifest.totalSeriesCount,
        totalRowCount: hiddenContext.experimentSampleData.manifest.totalRowCount,
        bundles: (hiddenContext.experimentSampleData.manifest.bundles ?? [])
          .map(bundle => ({
            bundleKey: bundle.bundleKey.trim(),
            filename: bundle.filename.trim(),
            replicateThingieId: bundle.replicateThingieId.trim(),
            replicateName: bundle.replicateName.trim(),
            seriesCount: bundle.seriesCount,
            taskCount: bundle.taskCount,
            rowCount: bundle.rowCount
          }))
          .filter(bundle => bundle.bundleKey && bundle.filename && bundle.replicateThingieId && bundle.replicateName),
        truncated: hiddenContext.experimentSampleData.manifest.truncated === true ? true : undefined
      }
      : undefined;
    const experimentAnnotations = serializeExperimentAnnotationsContext(hiddenContext.experimentAnnotations);

    return {
      thingieId: hiddenContext.thingieId.trim(),
      thingieName: hiddenContext.thingieName.trim(),
      experimentName: hiddenContext.experimentName.trim(),
      availableSeries,
      currentTabs,
      templateName: hiddenContext.templateName?.trim() || undefined,
      truncated: hiddenContext.truncated === true ? true : undefined,
      experimentSummary,
      experimentComparison,
      experimentSampleData: experimentSampleManifest ? { manifest: experimentSampleManifest } : undefined,
      experimentAnnotations
    };
  }

  private serializeExperimentSampleToolContext(hiddenContext?: ChatHiddenContext): Record<string, unknown> | undefined {
    const payloads = hiddenContext?.graphAgentContext?.experimentSampleData?.payloads
      ?.map(payload => ({
        bundleKey: payload.bundleKey.trim(),
        filename: payload.filename.trim(),
        replicateThingieId: payload.replicateThingieId.trim(),
        replicateName: payload.replicateName.trim(),
        seriesCount: payload.seriesCount,
        taskCount: payload.taskCount,
        rowCount: payload.rowCount,
        csvContent: payload.csvContent.trim()
      }))
      .filter(payload => payload.bundleKey && payload.filename && payload.replicateThingieId && payload.replicateName && payload.csvContent);
    if (!payloads?.length) {
      return undefined;
    }

    return {
      samplePointsPerSeries: hiddenContext?.graphAgentContext?.experimentSampleData?.manifest.samplePointsPerSeries,
      bundles: payloads
    };
  }

  // Conversation management methods
  public getConversations(scope?: IChatConversationScope | null): Observable<IChatConversation[]> {
    if (this.isScopedConversationScope(scope)) {
      return this.httpClient.post<IChatConversation[]>(
        this.scopedConversationUrls.scopedConversationsQuery,
        { thingieIds: scope.linkedThingieIds }
      ).pipe(
        map(conversations => this.sortConversations(conversations))
      );
    }

    return this.userDataService.getAllUserData('CHAT_CONVERSATION')
      .pipe(
        map(userDataItems => userDataItems.map(item => item.value)),
        map(conversations => conversations.filter(conversation => !this.isScopedSharedConversation(conversation))),
        map(conversations => this.sortConversations(conversations))
      );
  }

  public getConversation(id: string, scope?: IChatConversationScope | null): Observable<IChatConversation | null> {
    if (this.isScopedConversationScope(scope)) {
      return this.getScopedConversation(id);
    }

    return this.userDataService.getAllUserData('CHAT_CONVERSATION')
      .pipe(
        map(userDataItems => userDataItems.map(item => item.value)),
        map(conversations => conversations.find(conversation => conversation.id === id && !this.isScopedSharedConversation(conversation)) || null),
        switchMap(conversation => {
          if (conversation) {
            return of(conversation);
          }
          return this.getScopedConversation(id);
        })
      );
  }

  public saveConversation(conversation: IChatConversation): Observable<IChatConversation> {
    if (this.isScopedSharedConversation(conversation)) {
      return this.saveScopedConversationDocument(conversation)
        .pipe(
          switchMap(savedConversation => from(this.syncCopilotConversationReferences(savedConversation))
            .pipe(
              switchMap(referenceAnnotations => {
                if (this.hasSameReferenceAnnotations(savedConversation.referenceAnnotations, referenceAnnotations)) {
                  return of({
                    ...savedConversation,
                    referenceAnnotations
                  });
                }
                return this.saveScopedConversationDocument({
                  ...savedConversation,
                  referenceAnnotations
                });
              })
            ))
        );
    }

    return from(this.auth.getCurrentUserId()).pipe(
      switchMap(userId => this.userDataService.getAllUserData('CHAT_CONVERSATION').pipe(
        switchMap(existingItems => {
          const existingItem = existingItems.find(item => item.value.id === conversation.id);

          if (existingItem && existingItem._id) {
            return this.userDataService.updateUserData(
              'CHAT_CONVERSATION',
              userId,
              existingItem._id,
              conversation
            ).pipe(map(result => result.value));
          } else {
            return this.userDataService.createUserData(
              'CHAT_CONVERSATION',
              userId,
              conversation,
              'PRIVATE'
            ).pipe(map(result => result.value));
          }
        })
      ))
    );
  }

  public deleteConversation(id: string): Observable<void> {
    return this.getConversation(id)
      .pipe(
        switchMap(conversation => {
          if (conversation === null) {
            return of(undefined);
          }

          if (this.isScopedSharedConversation(conversation)) {
            return from(this.thingieService.removeCopilotConversationReferences({
              conversationId: conversation.id,
              thingieIds: conversation.linkedThingieIds ?? [],
              processIdx: 0
            }))
              .pipe(
                switchMap(() => this.httpClient.delete<void>(
                  this.scopedConversationUrls.scopedConversation.replace('{conversationId}', encodeURIComponent(id))
                ))
              );
          }

          return this.deleteGenericConversation(id);
        })
      );
  }

  private deleteGenericConversation(id: string): Observable<void> {
    return from(this.auth.getCurrentUserId()).pipe(
      switchMap(userId => this.userDataService.getAllUserData('CHAT_CONVERSATION').pipe(
        switchMap(existingItems => {
          const existingItem = existingItems.find(item => item.value.id === id);

          if (existingItem && existingItem._id) {
            return this.userDataService.deleteUserData('CHAT_CONVERSATION', userId, existingItem._id);
          }
          return of(undefined);
        })
      ))
    );
  }

  private getScopedConversation(id: string): Observable<IChatConversation | null> {
    return this.httpClient.get<IChatConversation>(
      this.scopedConversationUrls.scopedConversation.replace('{conversationId}', encodeURIComponent(id))
    ).pipe(
      catchError(() => of(null))
    );
  }

  private saveScopedConversationDocument(conversation: IChatConversation): Observable<IChatConversation> {
    return this.httpClient.post<IChatConversation>(
      this.scopedConversationUrls.scopedConversation.replace('/{conversationId}', ''),
      conversation
    );
  }

  private async syncCopilotConversationReferences(
    conversation: IChatConversation
  ): Promise<IConversationReferenceAnnotation[]> {
    if (!conversation.linkedThingieIds || conversation.linkedThingieIds.length === 0) {
      return conversation.referenceAnnotations ?? [];
    }

    const ownerId = conversation.ownerUserId ?? await this.auth.getCurrentUserId();
    const payload: ICopilotConversationReferenceSyncRequest = {
      conversationId: conversation.id,
      thingieIds: conversation.linkedThingieIds,
      processIdx: 0,
      title: conversation.title,
      preview: this.getConversationPreview(conversation),
      updatedAt: conversation.updatedAt,
      ownerId,
      provider: conversation.provider,
      model: conversation.modelUsed
    };

    const result = await this.thingieService.syncCopilotConversationReferences(payload);
    return result.locations.map(location => ({
      thingieId: location.thingieId,
      processIdx: location.processIndex,
      annotationIdx: location.annotationIndex
    }));
  }

  private getConversationPreview(conversation: IChatConversation): string {
    const firstUserMessage = conversation.messages.find(message => message.role === 'user');
    if (!firstUserMessage) {
      return '';
    }
    if (typeof firstUserMessage.content === 'string') {
      return firstUserMessage.content;
    }
    const textItem = firstUserMessage.content.find(item => item.type === 'text');
    return textItem?.text || '';
  }

  private hasSameReferenceAnnotations(
    currentReferences: IConversationReferenceAnnotation[] | undefined,
    nextReferences: IConversationReferenceAnnotation[]
  ): boolean {
    if ((currentReferences?.length ?? 0) !== nextReferences.length) {
      return false;
    }

    const normalizedCurrent = [...(currentReferences ?? [])]
      .sort((a, b) => a.thingieId.localeCompare(b.thingieId)
        || a.processIdx - b.processIdx
        || a.annotationIdx - b.annotationIdx);
    const normalizedNext = [...nextReferences]
      .sort((a, b) => a.thingieId.localeCompare(b.thingieId)
        || a.processIdx - b.processIdx
        || a.annotationIdx - b.annotationIdx);

    return normalizedCurrent.every((reference, index) => {
      const nextReference = normalizedNext[index];
      return reference.thingieId === nextReference.thingieId
        && reference.processIdx === nextReference.processIdx
        && reference.annotationIdx === nextReference.annotationIdx;
    });
  }

  private isScopedSharedConversation(conversation: IChatConversation): boolean {
    return conversation.sharingMode === 'replicate_access'
      && (conversation.linkedThingieIds?.length ?? 0) > 0;
  }

  private isScopedConversationScope(scope: IChatConversationScope | null | undefined): scope is IChatConversationScope {
    return scope?.sharingMode === 'replicate_access'
      && (scope.linkedThingieIds?.length ?? 0) > 0;
  }

  private sortConversations(conversations: IChatConversation[]): IChatConversation[] {
    return [...conversations].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }
}
